/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Receipt, Crown, ChartLine, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

// EMERALD-RECEIPT theme — distinct from Demographics (cobalt), Revenue (gold),
// Ads (ember).
const TH = {
  bg:        "#06120E",
  surface:   "#0F1F19",
  surfaceHi: "#172E25",
  border:    "#1F3A30",
  borderHi:  "#2D5544",
  accent:    "#4ADE9C",      // emerald
  accentDim: "#1F8A60",
  warm:      "#FFD17B",
  pop:       "#A8F0CF",
  pink:      "#F472B6",
  blue:      "#7DD3FC",
  text:      "#EAFBF2",
  textMute:  "#8DAA9C",
  textDim:   "#5A7468",
};

const LIMITS = [10, 20, 30, 50];

const TYPE_BADGE = {
  tool_unlock:  { label: "Tool unlock",  color: "#4ADE9C", icon: <Receipt size={11} weight="bold" /> },
  twenty_year:  { label: "20-Year PDF",  color: "#FFD17B", icon: <ChartLine size={11} weight="bold" /> },
  vip_pack:     { label: "VIP King",     color: "#F472B6", icon: <Crown size={11} weight="fill" /> },
};
const PAID_VIA_BADGE = {
  razorpay: { label: "Razorpay", color: "#7DD3FC" },
  wallet:   { label: "Wallet",   color: "#FFD17B" },
  vip:      { label: "VIP free", color: "#F472B6" },
};

export default function AdminRecentPurchasesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Recent Purchases is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    api.get("/super-admin/recent-purchases", { params: { limit } })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load purchases."))
      .finally(() => setLoading(false));
  }, [user, limit]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <button type="button" onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] mb-7 transition-opacity hover:opacity-100"
          style={{ color: TH.textMute, opacity: 0.7 }}
          data-testid="purchases-back">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] mb-5"
          style={{ backgroundColor: `${TH.accent}1A`, color: TH.accent, border: `1px solid ${TH.accentDim}` }}>
          <Receipt size={11} weight="bold" /> Super-Admin · Recent Purchases
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          Recent <span className="italic" style={{ color: TH.accent }}>purchased reports</span>
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed font-light" style={{ color: TH.textMute }}>
          Most-recent paid reports across every revenue stream — tool unlocks, the
          <b style={{ color: TH.text }}> 20-Year Vedic Forecast PDF</b>, and the
          <b style={{ color: TH.text }}> VIP King Pack</b>. Sorted newest-first. Wallet top-ups
          are intentionally excluded — they're credit additions, not report purchases (see Revenue page).
        </p>

        {/* Limit selector */}
        <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="purchases-limit-tabs">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] mr-1"
            style={{ color: TH.textDim }}>Show top</span>
          {LIMITS.map((n) => (
            <button key={n} type="button" onClick={() => setLimit(n)}
              className="px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider transition-all"
              style={{
                backgroundColor: limit === n ? TH.accent : "transparent",
                color: limit === n ? TH.bg : TH.textMute,
                border: `1px solid ${limit === n ? TH.accent : TH.border}`,
                fontWeight: limit === n ? 700 : 400,
              }}
              data-testid={`purchases-limit-${n}`}>
              {n}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-10 text-center font-mono text-sm" style={{ color: TH.textDim }}>
            Loading recent purchases…
          </div>
        )}

        {!loading && data && data.purchases.length === 0 && (
          <div className="mt-10 rounded-xl p-8 text-center" style={{ background: TH.surface, border: `1px solid ${TH.border}`, color: TH.textMute }}>
            <Sparkle size={28} weight="duotone" className="mx-auto mb-2" style={{ color: TH.accent }} />
            <div className="text-sm">No purchased reports yet. Wait for your first conversion or run a quick test purchase.</div>
          </div>
        )}

        {!loading && data && data.purchases.length > 0 && (
          <PurchasesList rows={data.purchases} />
        )}
      </main>
      <Footer />
    </div>
  );
}

// =============================================================================
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};
const relTime = (iso) => {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const Initials = ({ name }) => {
  const parts = (name || "—").trim().split(/\s+/);
  const initials = (parts[0]?.[0] || "?") + (parts[1]?.[0] || "");
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center font-mono text-xs font-bold flex-shrink-0"
      style={{ background: TH.surfaceHi, color: TH.accent, border: `1px solid ${TH.border}` }}>
      {initials.toUpperCase()}
    </div>
  );
};

const PurchasesList = ({ rows }) => (
  <div className="mt-7 rounded-xl overflow-hidden" style={{ background: TH.surface, border: `1px solid ${TH.border}` }}
    data-testid="purchases-list">
    <div className="px-4 sm:px-5 py-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]"
      style={{ color: TH.textDim, background: TH.surfaceHi, borderBottom: `1px solid ${TH.border}` }}>
      <span>#</span>
      <span>{rows.length} {rows.length === 1 ? "purchase" : "purchases"}</span>
    </div>
    <ul>
      {rows.map((r, i) => {
        const tb = TYPE_BADGE[r.type] || { label: r.type, color: TH.accent };
        const pv = PAID_VIA_BADGE[r.paid_via] || { label: r.paid_via, color: TH.textMute };
        return (
          <li key={`${r.type}-${r.fingerprint || r.user_id}-${r.purchased_at}-${i}`}
            className="px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-4 transition-colors"
            style={{ borderBottom: `1px solid ${TH.border}` }}
            data-testid={`purchases-row-${i + 1}`}>
            <div className="w-6 text-right font-mono text-xs flex-shrink-0" style={{ color: TH.textDim }}>{i + 1}</div>
            <Initials name={r.customer_name} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-sm truncate font-medium" style={{ color: TH.text }}>{r.customer_name}</span>
                <span className="font-mono text-[10px] truncate" style={{ color: TH.textMute }}>
                  {r.customer_email}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px]"
                  style={{ background: `${tb.color}1F`, color: tb.color, border: `1px solid ${tb.color}55` }}
                  data-testid={`purchases-type-${r.type}`}>
                  {tb.icon}{tb.label}
                </span>
                <span className="text-xs truncate" style={{ color: TH.pop }}>{r.product}</span>
                {r.subject_name && (
                  <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>
                    · subject "{r.subject_name}"
                  </span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-mono text-sm font-bold" style={{ color: TH.accent }}>
                ₹{Number(r.amount_inr).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
              <div className="font-mono text-[10px] mt-0.5" style={{ color: pv.color }}>
                {pv.label}
              </div>
            </div>
            <div className="hidden sm:block text-right flex-shrink-0 min-w-[150px]">
              <div className="font-mono text-[11px]" style={{ color: TH.textMute }}>{fmtDate(r.purchased_at)}</div>
              <div className="font-mono text-[10px]" style={{ color: TH.textDim }}>{relTime(r.purchased_at)}</div>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);
